/**
 * ServiceTreatmentForm — minimal editor for in-consult services.
 *
 * Services have no dosing, refills, or stock to allocate, so this form is
 * deliberately stripped down to just the service product, an optional zero-fee
 * waiver, and optional notes (persisted to `clinicalNotes` — `instructions`
 * carries OTC-dispensing semantics that don't apply to in-consult services).
 *
 * A service always submits as `internal_administered` (the backend forces
 * services to be administered in consult and bypasses stock allocation), with a
 * fixed quantity of 1 and a placeholder allocation to satisfy the API contract.
 */
import { useStore } from '@tanstack/react-form'
import { Loader2 } from 'lucide-react'
import { z } from 'zod'

import type { ClinicId } from '@snootsvet/shared'
import { calculateOrderTotal, formatRegionPrice } from '@snootsvet/shared'

import { useAppForm } from '@/frontend/components/form'
import { FieldWrapper } from '@/frontend/components/form/fields'
import { zodErrorToFieldErrors } from '@/frontend/components/form/validation'
import { Button } from '@/frontend/components/ui/button'
import { ProductWithStockSearch } from '@/frontend/features/inventory/product-search'
import { useViewerRegion } from '@/frontend/features/region/region-context'
import { useGetProduct } from '@/frontend/hooks/api/use-get-product'

import type { InternalAdministeredItem } from './treatments.types'

/** Pre-validation form state — all inputs nullable for the empty-form case. */
interface ServiceFormValues {
  productId: string | null
  zeroFee: boolean
  clinicalNotes: string | null
}

const SERVICE_DEFAULTS: ServiceFormValues = {
  productId: null,
  zeroFee: false,
  clinicalNotes: null,
}

/** Maps a saved service item back to pre-validation form state for editing. */
function toFormValues(item: InternalAdministeredItem): ServiceFormValues {
  return {
    productId: item.productId,
    zeroFee: item.zeroFee,
    clinicalNotes: item.clinicalNotes,
  }
}

const serviceSchema = z.object({
  productId: z.string({ message: 'Please select a service' }).min(1, 'Please select a service'),
  zeroFee: z.boolean(),
  clinicalNotes: z.string().nullable(),
})

interface ServiceTreatmentFormProps {
  defaultValues?: InternalAdministeredItem
  /**
   * Clinic the service is dispensed from. Services are only offered inside a
   * clinic consultation, so this is always provided in practice.
   */
  contextClinicId?: ClinicId
  onSave: (item: InternalAdministeredItem) => void
  onCancel: () => void
  editing?: boolean
  /** Whether an async save is in progress — disables the submit button. */
  saving?: boolean
}

export function ServiceTreatmentForm({
  defaultValues,
  contextClinicId,
  onSave,
  onCancel,
  editing = false,
  saving = false,
}: ServiceTreatmentFormProps) {
  const { region } = useViewerRegion()
  const defaults = defaultValues ? toFormValues(defaultValues) : SERVICE_DEFAULTS

  const form = useAppForm({
    defaultValues: defaults,
    validators: {
      onSubmit: ({ value }) => {
        const res = serviceSchema.safeParse(value)
        if (!res.success) return zodErrorToFieldErrors(res.error)
      },
    },
    onSubmit: ({ value }) => {
      if (!contextClinicId) return
      const parsed = serviceSchema.parse(value)
      onSave({
        type: 'internal_administered',
        treatmentCategory: 'service',
        productId: parsed.productId!,
        quantity: 1,
        clinicId: contextClinicId,
        // Services have no stock to allocate; the backend bypasses allocation for
        // service products, but the API contract still requires at least one entry.
        allocations: [{ batchNumber: null, quantity: 1 }],
        zeroFee: parsed.zeroFee,
        // The Notes field maps to clinicalNotes; administered items carry no instructions.
        clinicalNotes: parsed.clinicalNotes?.trim() ? parsed.clinicalNotes.trim() : null,
        internalNotes: null,
        // Services are never vaccines — tag number / injection site don't apply.
        tagNumber: null,
        injectionSite: null,
        reminders: [],
        product: product ?? null,
      })
    },
  })

  const productId = useStore(form.store, (s) => s.values.productId)
  const zeroFee = useStore(form.store, (s) => s.values.zeroFee)
  const discountBps = zeroFee ? 10_000 : 0

  // Fetch the selected service to show its charge and snapshot it on save. Keyed
  // on productId so create and edit flows resolve through the same query.
  const { data: product } = useGetProduct(productId, { include: 'stock' })

  const unitPrice = product?.unitPrice ?? 0
  const totalPrice = calculateOrderTotal({ unitPrice, quantity: 1, discountBps })
  // Prices are denominated in the product's region; fall back to the viewer's
  // region only in the brief window before the product has loaded.
  const priceRegion = product?.region ?? region
  const canSubmit = !!contextClinicId

  return (
    <form.AppForm>
      <div className="flex flex-col gap-3">
        {/* Service selector */}
        <form.AppField name="productId">
          {(field) => (
            <FieldWrapper
              field={field}
              label="Service"
              required
              render={({ id, disabled }) => (
                <ProductWithStockSearch
                  id={id}
                  value={product ?? null}
                  onChange={(p) => field.handleChange(p ? p.id : null)}
                  type={['service']}
                  clinicId={contextClinicId}
                  onBlur={field.handleBlur}
                  aria-invalid={field.state.meta.errors.length > 0}
                  disabled={disabled || editing}
                  placeholder="Search services..."
                />
              )}
            />
          )}
        </form.AppField>

        {/* Charge preview */}
        {unitPrice > 0 && product && (
          <p className="text-muted-foreground">
            Charge:{' '}
            <span className="font-semibold text-foreground">
              {formatRegionPrice(totalPrice, priceRegion)}
            </span>
            {discountBps > 0 && (
              <>
                {' '}
                <s>
                  {formatRegionPrice(
                    calculateOrderTotal({ unitPrice, quantity: 1, discountBps: 0 }),
                    priceRegion
                  )}
                </s>
                {` (${discountBps / 100}% discount)`}
              </>
            )}
          </p>
        )}

        {/* Zero fee */}
        <form.AppField name="zeroFee">
          {(field) => (
            <field.CheckboxField label="Zero fee" description="Waive the charge for this service" />
          )}
        </form.AppField>

        {/* Notes — optional, stored as clinicalNotes */}
        <form.AppField name="clinicalNotes">
          {(field) => (
            <field.TextareaField
              label="Notes"
              placeholder="Optional notes about this service"
              rows={2}
            />
          )}
        </form.AppField>

        {/* Actions */}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" size="lg" onClick={onCancel}>
            Cancel
          </Button>
          <form.ConfirmSubmitButton
            type="button"
            disabled={saving || !canSubmit}
            heading="Confirm service"
            description={
              <div className="flex flex-col gap-2">
                <p>
                  This service will be administered in consult, deducting stock immediately. It can
                  no longer be edited or deleted once payment has been taken.
                </p>
                <dl className="grid grid-cols-[auto_1fr] gap-x-4">
                  <dt className="font-medium text-foreground">Service:</dt>
                  <dd>{product?.name ?? 'Unknown'}</dd>
                  {discountBps > 0 && (
                    <>
                      <dt className="font-medium text-foreground">Discount:</dt>
                      <dd>{discountBps / 100}%</dd>
                    </>
                  )}
                  <dt className="font-medium text-foreground">Charge:</dt>
                  <dd>{formatRegionPrice(totalPrice, priceRegion)}</dd>
                </dl>
              </div>
            }
            confirmLabel="Save"
          >
            {saving ? <Loader2 className="size-3.5 mr-1.5 animate-spin" /> : null}
            Save
          </form.ConfirmSubmitButton>
        </div>
      </div>
    </form.AppForm>
  )
}
